import { useNavigate } from "react-router-dom";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { STATIC_STRINGS } from "@asez/shared";
import {GenericToast} from "@asez/component-library";
import { useStepper } from "./hooks/useStepper";

const steps = [
  STATIC_STRINGS.ASSESSMENT_DETAILS,
  STATIC_STRINGS.TOPIC_DISTRIBUTION,
  STATIC_STRINGS.PREVIEW_QUESTIONS,
  STATIC_STRINGS.SCHEDULE_TIMINGS,
  STATIC_STRINGS.ADD_CANDIDATES,
  STATIC_STRINGS.FINAL_PREVIEW,
];

const ProgressStepper = ({
  activeStep,
  handleStepChange,
}: {
  activeStep: number;
  handleStepChange: (arg: number) => void;
}) => {
  const navigate = useNavigate();
  const {
    completed,
    handlePrev,
    handleNext,
    isToastOpen,
    setToastOpen,
    title,
    status,
  } = useStepper({ activeStep, handleStepChange });

  return (
    <>
      <div className="h-fit">
        <div className="w-full px-4 py-2 border-b-[1px] border-neutral-dark flex justify-between">
          <div className="max-w-4/5 flex flex-col justify-center items-start">
            <button
              className="cursor-pointer text-neutral-dark w-16 flex justify-between items-center px-1 font-medium text-sm xl:text-base"
              onClick={() => navigate("/assessments")}
            >
              <ArrowBackIcon /> {STATIC_STRINGS.BACK}
            </button>
            <div className="flex justify-center items-center">
              <h1 className="text-2xl xl:text-4xl font-semibold py-2 max-w-[90%]">
                {`${
                  activeStep === 1 ? STATIC_STRINGS.UNTITLED_ASSESSMENT : title
                }`}
              </h1>
              <div className={`mx-2 text-sm ${status.toLowerCase()}`}>
                {status}
              </div>
            </div>
          </div>
          <div className="min-w-1/5 flex items-center justify-end">
            <button
              onClick={handlePrev}
              className={`btn px-2 py-1 xl:px-3 xl:py-2 shadow-md text-sm whitespace-nowrap ${
                activeStep === 1
                  ? `btn-outline-disabled`
                  : `btn-primary border-2 border-neutral-dark`
              } mr-2`}
              disabled={activeStep === 0}
            >
              <ChevronLeftIcon />
              {STATIC_STRINGS.PREVIOUS_STEP}
            </button>
            <button
              onClick={handleNext}
              className={`btn border-2 shadow-md border-neutral-dark btn-primary px-2 py-1 xl:px-3 xl:py-2 text-sm whitespace-nowrap ${
                completed.length >= steps.length &&
                `btn-outline-disabled pointer-events-none`
              }`}
            >
              {completed.length >= steps.length - 1
                ? STATIC_STRINGS.FINISH
                : STATIC_STRINGS.NEXT_STEP}
              <ChevronRightIcon />
            </button>
          </div>
        </div>
        <Stepper
          activeStep={activeStep - 1}
          alternativeLabel
          className="mt-6 mb-4 xl:mt-12 xl:mb-6"
        >
          {steps.map((step, index) => (
            <Step
              key={step}
              completed={completed.includes(index)}
              sx={{
                "& .MuiStepLabel-root .Mui-completed": {
                  color: "var(--color-success)",
                },
                "& .MuiStepLabel-root .Mui-active": {
                  color: "var(--color-neutral-dark)",
                },
                "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel":
                  {
                    color: "var(--color-text)",
                    fontWeight: "600",
                    fontSize: {
                      xs: "0.875rem",
                      lg: "1rem",
                      xl: "1.2rem",
                    },
                  },
                "& .MuiStepLabel-label": {
                  fontSize: {
                    xs: "0.875rem",
                    lg: "1rem",
                    xl: "1.2rem",
                  },
                },
                "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
                  fill: "var(--color-white)",
                },
              }}
            >
              <StepLabel>{step}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>
      <GenericToast
        open={isToastOpen}
        onCloseHandler={() => setToastOpen(false)}
        label={STATIC_STRINGS.FORM_VALIDATION_ERROR}
      />
    </>
  );
};

export default ProgressStepper;
